import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
    FiLifeBuoy, FiArrowRight, FiUsers, FiDatabase, FiCpu,
    FiHeadphones,
    FiClock,
    FiRefreshCw,
    FiShield
} from "react-icons/fi";
import { BsFillRocketTakeoffFill } from "react-icons/bs";
import { Container, Row, Col } from "reactstrap";

import "./ProductSupportServices.css";

const ProductSupportServices = () => {
    const heroRef = useRef(null);
    const heroInView = useInView(heroRef, { once: true });
    const supportServices = [
        { icon: FiUsers, title: "Professional Services", desc: "Expert consulting and technical advisory services for your products." },
        { icon: FiHeadphones, title: "L1–L4 Technical Support", desc: "Multi-tier technical support from basic troubleshooting to deep engineering fixes." },
        { icon: FiClock, title: "24×7 Technical Support", desc: "Round-the-clock support ensuring your products are always running at peak performance." },
        { icon: FiClock, title: "Bug Fixing & Feature Dev", desc: "Rapid bug resolution and feature enhancements to keep your products competitive." },
        { icon: FiRefreshCw, title: "Customer Onboarding", desc: "Streamlined onboarding cycles to get your customers up and running quickly." },
        { icon: FiShield, title: "Quality Assurance", desc: "Continuous testing and quality monitoring to maintain product excellence." },
    ];

    const related = [
        { icon: BsFillRocketTakeoffFill, title: "New Product Development", to: "/services/new-product-development" },
        { icon: FiDatabase, title: "Cloud Solutions", to: "/services/cloud-solutions" },
        { icon: FiCpu, title: "AI Enabled GCC Services", to: "/services/gcc-services" },
    ];

    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" }
        })
    };
    // Services Grid Section
    const servicesRef = useRef(null);
    const servicesInView = useInView(servicesRef, { once: false, margin: "-100px" });

    return (
        <div>
            <section
                ref={heroRef}
                className="product-support-hero position-relative overflow-hidden d-flex align-items-center"
            //   style={{ minHeight: "85vh" }}
            >
                {/* Light Background */}
                <div className="product-support-bg" />

                {/* Animated Glows */}
                <motion.div
                    className="product-support-glow-1"
                    animate={{ scale: [1, 1.12, 1], x: [0, 25, 0] }}
                    transition={{ duration: 9, repeat: Infinity }}
                />
                <motion.div
                    className="product-support-glow-2"
                    animate={{ scale: [1.08, 1, 1.08] }}
                    transition={{ duration: 12, repeat: Infinity }}
                />

                <Container className="position-relative">
                    <Row className="align-items-center g-5">
                        {/* Left Side - Text */}
                        <Col lg={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                                transition={{ duration: 0.8 }}
                            >
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={heroInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ delay: 0.2 }}
                                    className="product-support-badge d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill mb-4"
                                >
                                    <FiLifeBuoy size={18} /> Product Support Services
                                </motion.div>

                                <h1 className="product-support-hero-title fw-bold mb-3">
                                    Transforming Product Support into
                                    <br />
                                    <span className="product-support-highlight position-relative">
                                        Growth & Performance
                                        {/* Curved Underline */}
                                        <motion.svg
                                            className="product-support-underline"
                                            viewBox="0 0 380 20"
                                            initial={{ pathLength: 0 }}
                                            animate={heroInView ? { pathLength: 1 } : {}}
                                            transition={{ delay: 0.6, duration: 1.2 }}
                                        >
                                            <motion.path
                                                d="M1 12 Q 95 2 190 12 T 370 12"
                                                fill="none"
                                                stroke="url(#gradientStroke)"
                                                strokeWidth="6"
                                                strokeLinecap="round"
                                            />
                                            <defs>
                                                <linearGradient id="gradientStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="#2bb677" />
                                                    <stop offset="100%" stopColor="#465ba0" />
                                                </linearGradient>
                                            </defs>
                                        </motion.svg>
                                    </span>
                                </h1>

                                <p className="product-support-hero-text lead mb-5">
                                    Our Product Support Services help clients extend the life of core products,
                                    improve performance, and amplify market relevance, revenue potential, and profitability.
                                </p>

                                <Link to="/contact" className="product-support-cta-btn d-inline-flex align-items-center gap-2">
                                  Connect with us <FiArrowRight size={22} />
                                </Link>
                            </motion.div>
                        </Col>

                        {/* Right Side - Tier Cards */}
                        <Col lg={6}>
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.9, delay: 0.3 }}
                                className="product-support-tiers"
                            >
                                {[
                                    { level: "01", title: "Professional Services", desc: "Expert consulting & advisory services" },
                                    { level: "02", title: "L1-L4 Technical Support.", desc: "Multi-tier technical support" },
                                    { level: "03", title: "24x7 Technical Support", desc: "Round-the-clock support" },
                                    { level: "04", title: "Bug Fixing & Feature Dev", desc: "Rapid bug resolution & feature enhancements" },
                                    { level: "05", title: "Customer Onboarding Cycle", desc: "Streamlined onboarding cycles" },
                                    
                                ].map((tier, i) => (
                                    <motion.div
                                        key={tier.level}
                                        initial={{ opacity: 0, x: 40 }}
                                        animate={heroInView ? { opacity: 1, x: 0 } : {}}
                                        transition={{ delay: 0.4 + i * 0.1 }}
                                        whileHover={{ x: -8, scale: 1.02 }}
                                        className="product-support-tier-card d-flex align-items-center gap-4"
                                    >
                                        <div className="product-support-tier-level">
                                            {tier.level}
                                        </div>
                                        <div className="flex-grow-1">
                                            <h4 className="product-support-tier-title mb-1">{tier.title}</h4>
                                            <p className="product-support-tier-desc mb-0">{tier.desc}</p>
                                        </div>
                                        <FiArrowRight className="product-support-tier-arrow" />
                                    </motion.div>
                                ))}
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>
            {/* Services Grid - Modernized Cards */}
            <section ref={servicesRef} className="product-support-services py-5 py-lg-6">
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                        className="text-center mb-5"
                    >
                        <span className="product-support-services-badge d-inline-block px-4 py-2 rounded-pill mb-3">
                            What We Offer
                        </span>
                        <h2 className="product-support-services-title fw-bold mb-3">
                            Comprehensive Support Solutions
                        </h2>
                        <p className="product-support-services-subtitle text-muted mx-auto" style={{ maxWidth: "620px" }}>
                            We develop product roadmaps that leverage modern technologies, allowing customers to focus on new product portfolios.
                        </p>
                    </motion.div>

                    <Row className="g-4">
                        {supportServices.map((svc, i) => (
                            <Col key={i} md={6} lg={4}>
                                <motion.div
                                    custom={i}
                                    initial="hidden"
                                    animate={servicesInView ? "visible" : "hidden"}
                                    variants={fadeUp}
                                    whileHover={{
                                        y: -12,
                                        rotateX: -4,
                                        rotateY: 6,
                                        transition: { duration: 0.4 }
                                    }}
                                    style={{ perspective: "1200px" }}
                                    className="product-support-service-card h-100"
                                >
                                    <div className="position-relative h-100 rounded-4 overflow-hidden border border-light">
                                        {/* Hover Border Glow */}
                                        <div className="product-support-service-glow" />

                                        <div className="product-support-service-inner p-4 h-100 d-flex flex-column">
                                            <div className="product-support-service-icon-wrapper mb-4">
                                                <svc.icon size={32} />
                                            </div>

                                            <h3 className="product-support-service-title fw-bold mb-3">
                                                {svc.title}
                                            </h3>

                                            <p className="product-support-service-desc flex-grow-1">
                                                {svc.desc}
                                            </p>

                                            {/* Bottom Accent Line */}
                                            <div className="product-support-service-line mt-auto" />
                                        </div>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Related Services */}
            {/* <section className="product-support-related py-5 py-lg-6 bg-light">
                <Container>
                    <h2 className="product-support-related-title text-center fw-bold mb-5">
                        Related Services
                    </h2>

                    <Row className="g-4">
                        {[
                            { title: "New Product Development", to: "/services", icon: BsFillRocketTakeoffFill },
                            { title: "Cloud Solutions", to: "/cloud-solutions", icon: FiDatabase },
                            { title: "AI Enabled GCC Services", to: "/gcc-services", icon: FiCpu },
                        ].map((svc, i) => (
                            <Col key={i} md={6} lg={4}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.15 }}
                                    whileHover={{ y: -8, scale: 1.02 }}
                                >
                                    <Link to={svc.to} className="product-support-related-card text-decoration-none">
                                        <div className="product-support-related-inner text-center p-4 h-100">
                                            <div className="product-support-related-icon-wrapper mx-auto mb-4">
                                                <svc.icon size={42} />
                                            </div>
                                            <h3 className="product-support-related-title mb-3">
                                                {svc.title}
                                            </h3>
                                            <span className="product-support-related-link d-inline-flex align-items-center gap-2">
                                                Learn More <FiArrowRight size={18} />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section> */}
            <section className="npd-section npd-section--alt">
                <div className="npd-container">
                    <motion.h2
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                        className="npd-related-title"
                    >
                        Related Services
                    </motion.h2>
                    <div className="npd-related-grid">
                        {related.map((r, i) => (
                            <motion.div
                                key={r.title} custom={i}
                                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                                className="npd-related-card"
                            >
                                <div className="npd-related-icon"><r.icon size={22} /></div>
                                <h4>{r.title}</h4>
                                <Link to={r.to} className="npd-related-link">
                                    Learn more <FiArrowRight size={16} />
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductSupportServices;